import { InstPage, TgPage } from '@/features/pages';
import { Departments, Platforms } from '@/types';

export namespace prophetTypes {
  export type Page = {
    _id: string;
    /** @deprecated */
    name?: string;
    account: {
      _id: string;
    };
    appPage:
      | {
          _id: string;
        }
      | TgPage
      | InstPage;
    config: {
      isEnabled: boolean;
      isKeywordsEnabled: boolean;
      isRefusesEnabled?: boolean;
      aiId?: number;
      /** Absent field = night mode enabled */
      isNightModeEnabled?: boolean;
      limits?: {
        perMinute?: number;
        perHour?: number;
        perDay?: number;
      };
    };
    funnels: {
      completedNewDialog: {
        _id: string;
      }[];
      completedNewDialogWithRefuse?: {
        _id: string;
      }[];
      manager: {
        _id: string;
      }[];
      underage?: {
        _id: string;
      }[];
    };
    platform: Platforms;
    department: Departments;
  };

  export type RuleRequirementDate = {
    type: 'date';
    count: number[];
    texts: string[];
  };
  export type RuleRequirementImage = {
    type: 'image';
    count: number[];
    texts: string[];
  };
  export type RuleRequirementText = {
    type: 'text';
    texts: string[];
  };
  export type Rule = {
    _id: string;
    page: {
      _id: string;
    };
    keyword?: string;
    requirements: (RuleRequirementDate | RuleRequirementImage | RuleRequirementText)[];
    actions: {
      success:
        | {
            type: 'sendToManager';
            funnelId: string;
          }
        | {
            type: 'reply';
          };
      fail: 'sendToManager' | 'reply';
      noResponse: 'reply' | 'remove';
    };
    intervals: {
      requirements: number;
    };
    messages: {
      greetings: RuleMessage[][];
      preparing?: RuleMessage[][];
      spreads: RuleMessage[][];
      discounts?: RuleMessage[][];
      spreadKicks?: RuleMessage[][];
    };
    isDiscountRequired?: boolean;
    isSpreadKickRequired?: boolean;
  };
  export type KeywordRule = {
    _id: string;
    page: {
      _id: string;
    };
    keyword: string;
    messages: {
      spreads: RuleMessage[][];
      /** If received same keyword again */
      repeatedKeyword: RuleMessage[][];
    };
  };
  export type RuleMessageText = { type: 'text'; text: string };
  export type RuleMessageImage = { type: 'image'; url: string } | { type: 'image'; file: File };
  export type RuleMessage = RuleMessageText | RuleMessageImage;

  export type Stats = {
    _id: string;
    page: {
      _id: string;
    };
    date: string;
    newDialogs: number;
    accepted: number;
    preparing: number;
    spreads: number;
    spreadKicks?: number;
    discounts: number;
    refuses: number;
    sentToManager: number;
    removed: number;
    keywords: Record<string, number>;
    spreadKeywords?: Record<string, number>;
  };

  export type KeywordStats = {
    _id: string;
    page: { _id: string };
    date: string;
    keywords: Record<string, number>;
  };

  export type GetProphetPages = (params: {
    platform: string;
    department: Departments;
  }) => Promise<{ pages: prophetTypes.Page[] }>;

  export type GetProphetPage = (pageId: string) => Promise<{ page: prophetTypes.Page }>;

  export type AddProphetPage = (data: {
    appPage: Page['appPage'];
    funnels: Page['funnels'];
    platform: Page['platform'];
    department: Page['department'];
    aiId: Page['config']['aiId'];
  }) => Promise<{ page: prophetTypes.Page }>;
  export type EditProphetPage = (data: {
    _id: string;
    funnels: Page['funnels'];
    /** null = clear the value (an omitted key means "keep as is" on the server) */
    aiId: number | null;
    isNightModeEnabled?: boolean;
    /** null = clear the value (back to system default / no limit) */
    limits?: {
      perMinute?: number | null;
      perHour?: number | null;
      perDay?: number | null;
    };
  }) => Promise<{ page: prophetTypes.Page }>;
  export type CloneProphetPage = (data: { pageId: string; ruleIds: string[] }) => Promise<void>;

  export type ToggleProphetPage = (data: { pageId: string; isEnabled: boolean }) => Promise<void>;
  export type ToggleKeywordsPage = (data: { pageId: string; isEnabled: boolean }) => Promise<void>;

  export type AddProphetRule = (data: Omit<Rule, '_id'>) => Promise<{ page: Page }>;
  export type EditProphetRule = (
    data: Omit<Rule, 'page'>,
    pageId: string
  ) => Promise<{ rule: Rule }>;
  export type GetProphetRules = (params: {
    pageId: string;
  }) => Promise<{ rules: prophetTypes.Rule[] }>;

  export type AddKeywordRule = (data: Omit<KeywordRule, '_id'>) => Promise<{ page: Page }>;

  export type EditKeywordRule = (
    data: Pick<KeywordRule, '_id' | 'messages'>,
    pageId: string
  ) => Promise<{ rule: KeywordRule }>;

  export type GetKeywordRules = (params: {
    pageId: string;
  }) => Promise<{ rules: prophetTypes.KeywordRule[] }>;

  export type DeleteRule = (params: { ruleId: string }) => Promise<void>;

  export type GetProphetStats = (params: {
    pageIds: string[];
    date?: string;
  }) => Promise<{ stats: Stats[] }>;

  export type GetProphetKeywordStats = (params: {
    pageId: string;
  }) => Promise<{ stats: KeywordStats[] }>;

  export type PageAuditRecord = {
    _id: string;
    pageId: string;
    userId: string;
    /** Account name resolved by the server from userId; absent for deleted accounts. */
    userName?: string;
    action: string;
    /** Only actually-changed paths, e.g. 'config.limits.perDay': { before: null, after: 500 } */
    changes: Record<string, { before: unknown; after: unknown }>;
    at: string;
  };

  export type GetProphetPageAudit = (params: {
    pageId: string;
    skip: number;
    limit: number;
  }) => Promise<{ records: PageAuditRecord[]; total: number }>;
}
